import { Container } from '@tryhackme/thm-ui-components';
import styled, { css, keyframes } from 'styled-components';

export const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  gap: 6rem;
  width: 100%;
  margin: 0 auto 23rem;

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    margin: 0 auto 15rem;
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    gap: 4.6rem;
  }
`;

export const StyledHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

export const StyledHeading = styled.h2`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
  line-height: 1.149;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 2.4rem;
  }
`;

export const StyledDescription = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 2rem;
  line-height: 1.257;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[400]};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.default};
  }
`;

export const StyledNavigationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.4rem;
  height: 4.4rem;
  background: ${({ theme }) => theme.colors.background.dark};
  border: 0.1rem solid ${({ theme }) => theme.colors.border.dark};
  border-radius: 0.4rem;
  color: ${({ theme }) => theme.colors.text.main};
  font-size: ${({ theme }) => theme.fonts.sizes.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary.lighter};
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const StyledNavigationWrapper = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2.4rem;
    width: 100%;
  }
`;

export const StyledCarouselWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.4rem;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    flex-direction: column-reverse;
    gap: 3.2rem;
  }

  /* Hide individual navigation buttons on mobile, show only the wrapper */
  > ${StyledNavigationButton} {
    @media (max-width: ${({ theme }) => theme.sizes.md}) {
      display: none;
    }
  }
`;

export const StyledCarouselContainer = styled.div`
  flex: 1;
  overflow: hidden;
  width: 100%;
  max-width: 132rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    order: 1;
  }
`;

export const StyledCarouselTrack = styled.div<{ $itemsPerSlide: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $itemsPerSlide }) => $itemsPerSlide}, 1fr);
  gap: 2.4rem;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    gap: 2.4rem;
  }
`;

const slideInFromRight = keyframes`
  from { transform: translateX(4rem); }
  to { transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { transform: translateX(-4rem); }
  to { transform: translateX(0); }
`;

export const StyledSecurityCard = styled.div<{ $direction?: 'next' | 'prev' }>`
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  padding: 3.2rem;
  background: ${({ theme }) => theme.colors.secondary.light};
  border-radius: 0.5rem;
  height: 100%;
  min-height: 40.7rem;
  will-change: transform, opacity;

  ${({ $direction }) =>
    $direction &&
    css`
      animation: ${$direction === 'next' ? slideInFromRight : slideInFromLeft} 300ms
        cubic-bezier(0.22, 1, 0.36, 1);
    `}

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 3.2rem;
    gap: 1.8rem;
  }
`;

export const StyledSecurityCardTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 2.4rem;
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 2.4rem;
  }
`;

export const StyledSecurityCardDescription = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.medium};
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.grey[400]};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.medium};
  }
`;

export const StyledSecurityCardPlaceholder = styled.div`
  width: 100%;
  height: 21.8rem;
  background: ${({ theme }) => theme.colors.secondary.lighter};
  border-radius: 1.5rem;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    height: 21.8rem;
  }
`;
